import axios from 'axios';
import { env } from '@/utils/constants/env';
import axiosRetry from 'axios-retry';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import Swal from 'sweetalert2';

const axiosInstance = axios.create({
  baseURL: env.apiTasklistBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - get fresh token from Keycloak on every request
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      // Get fresh token (Keycloak handles refresh internally)
      const token = await useAuthState.getState().getValidToken();
      const activeTenant = useTenantState.getState().currentTenant;

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (activeTenant) {
        config.headers['X-ACTIVE-TENANT'] = activeTenant.slug;
      }

      config.headers['Content-Type'] = 'application/json';

      return config;
    } catch (error) {
      console.error('Error in request interceptor:', error);
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Handle Trial Expired
    const responseData = error.response?.data;
    if (
      error.response?.status === 403 &&
      (responseData?.error === 'TRIAL_EXPIRED_BLOCKED' || responseData?.message === 'TRIAL_EXPIRED_BLOCKED')
    ) {
      const userProfile = useAuthState.getState().userProfile;
      const trialEndsAt = userProfile?.trial_ends_at;
      const formattedDate = trialEndsAt ? new Date(trialEndsAt).toISOString().split('T')[0] : '';

      Swal.fire({
        icon: 'error',
        title: 'Access Restricted',
        html: `
          <div class="flex flex-col gap-2">
            <p>Your trial has ended on <b>${formattedDate}</b>.</p>
            <p>Please contact support to continue using these features.</p>
            <div class="mt-4">
               <a href="${env.contactUrl}" target="_blank" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 no-underline inline-block">
                 Contact Support
               </a>
            </div>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        showCloseButton: true,
      });

      return Promise.reject(error);
    }

    // Handle 401 with token refresh via keycloak-js
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await useAuthState.getState().getValidToken();

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } else {
          console.warn('No valid token available after refresh attempt');
          globalThis.location.href = '/login';
          localStorage.removeItem('auth-storage');
          throw new Error('Authentication required');
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        globalThis.location.href = '/login';
        localStorage.removeItem('auth-storage');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

axiosRetry(axiosInstance, {
  retries: 2,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    return axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response?.status !== undefined && error.response.status >= 500 && error.response.status < 600);
  },
});

export { axiosInstance };
